import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["clean", "dirty"]

  static values = {
    message: { type: String, default: "You have unsaved changes. Are you sure you want to leave?" }
  }

  connect() {
    this.dirtyForms = new Set(this.element.querySelectorAll("form[data-form-guard-invalid]"))

    this.boundBeforeUnload = this.handleBeforeUnload.bind(this)
    this.boundTurboBeforeVisit = this.handleTurboBeforeVisit.bind(this)
    this.boundTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)

    window.addEventListener("beforeunload", this.boundBeforeUnload)

    document.addEventListener("turbo:before-visit", this.boundTurboBeforeVisit)

    document.addEventListener("turbo:submit-end", this.boundTurboSubmitEnd)

    this.render()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundTurboBeforeVisit)
    document.removeEventListener("turbo:submit-end", this.boundTurboSubmitEnd)
  }

  get isDirty() {
    return this.dirtyForms.size > 0
  }

  markDirty(event) {
    if (event.target.closest("[data-form-guard-ignore]")) {
      return
    }

    if (event.target.type === "hidden") {
      return
    }

    const form = event.target.form || event.target.closest("form")
    if (!form) {
      return
    }

    this.dirtyForms.delete(form)
    this.dirtyForms.add(form)
    this.render()
  }

  markClean() {
    this.dirtyForms.clear()
    this.render()
  }

  submit() {
    const form = this.lastDirtyForm()
    if (form) {
      form.requestSubmit()
    } else {
      this.render()
    }
  }

  // Een bulk-opslag vervangt het formulier door een nieuw element met hetzelfde id; zoek dat op.
  lastDirtyForm() {
    for (const form of [...this.dirtyForms].reverse()) {
      const current = form.isConnected ? form : (form.id && document.getElementById(form.id))
      if (current) {
        return current
      }
      this.dirtyForms.delete(form)
    }
    return null
  }

  render() {
    const dirty = this.isDirty
    this.dirtyTargets.forEach((element) => element.classList.toggle("hidden", !dirty))
    this.cleanTargets.forEach((element) => element.classList.toggle("hidden", dirty))
  }

  handleBeforeUnload(event) {
    if (this.isDirty) {
      event.preventDefault()
      event.returnValue = this.messageValue
      return this.messageValue
    }
  }

  handleTurboBeforeVisit(event) {
    if (this.isDirty) {
      const confirmed = window.confirm(this.messageValue)
      if (!confirmed) {
        event.preventDefault()
      } else {
        this.markClean()
      }
    }
  }

  handleTurboSubmitEnd(event) {
    const form = event.target
    if (event.detail?.fetchResponse?.succeeded) {
      for (const dirtyForm of [...this.dirtyForms]) {
        if (dirtyForm === form || (form.id && dirtyForm.id === form.id)) {
          this.dirtyForms.delete(dirtyForm)
        }
      }
      this.render()
    }
  }
}
